import type { I18nString } from '@affine/i18n';
import { GithubIcon, GoogleIcon, IssueIcon } from '@blocksuite/icons/rc';
import type { ReactNode } from 'react';

import { DevtoSettingPanel } from './devto/setting-panel';
import { GitHubSettingPanel } from './github/setting-panel';
import { GoogleSettingPanel } from './google/setting-panel';
import { LinearSettingPanel } from './linear/setting-panel';
import MCPIcon from './mcp-server/MCP.inline.svg';
import { McpServerSettingPanel } from './mcp-server/setting-panel';

const DevtoIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
    <path d="M7.42 10.05c-.18-.16-.46-.23-.84-.23H6v4.36h.58c.37 0 .65-.08.84-.23.18-.16.27-.45.27-.89v-1.13c0-.43-.09-.72-.27-.88zm.25 2.23c0 .29-.04.51-.12.65-.08.14-.21.23-.39.27-.08.02-.17.02-.28.02H6.8v-3.15h.58c.25 0 .43.06.54.18.11.12.17.34.17.67v1.36zM10.2 14.18V9.82h1.44v.44h-.96v1.36h.93v.44h-.93v1.68h.96v.44H10.2zm2.13-4.36h.56l.58 2.7h.02l.58-2.7h.56L13.5 14.18h-.54l-.63-4.36zm4.87.46c-.2-.17-.47-.26-.81-.26-.35 0-.62.09-.82.26-.2.17-.3.44-.3.8v2.05c0 .36.1.62.3.8.2.17.47.26.82.26.34 0 .61-.09.81-.26.2-.18.3-.44.3-.8V11.08c0-.36-.1-.63-.3-.8zm-.18 2.88c0 .18-.04.31-.12.41-.08.1-.2.15-.36.15-.16 0-.28-.05-.36-.15-.08-.1-.12-.23-.12-.41V11.05c0-.18.04-.31.12-.41.08-.1.2-.15.36-.15.16 0 .28.05.36.15.08.1.12.23.12.41v2.11zM0 4.85v14.3c0 2.68 2.17 4.85 4.85 4.85h14.3c2.68 0 4.85-2.17 4.85-4.85V4.85C24 2.17 21.83 0 19.15 0H4.85C2.17 0 0 2.17 0 4.85zM19.65 12c0 4.23-3.43 7.65-7.65 7.65-4.23 0-7.65-3.43-7.65-7.65 0-4.23 3.43-7.65 7.65-7.65 4.23 0 7.65 3.43 7.65 7.65z" />
  </svg>
);

const VSCodeIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
    <path d="M17.583 2.333L9.75 9.667l-4.583-3.5L2.333 7.5v9l2.834 1.333 4.583-3.5 7.833 7.334L21.667 19V5l-4.084-2.667zM6.167 14.667V9.333l2.916 2.667-2.916 2.667zm11.416 2.666L12.75 12l4.833-5.333v10.666z" />
  </svg>
);

type IntegrationCard = {
  id: string;
  /**
   * I18nString ONLY — deliberately not `| string`.
   *
   * Five cards took that escape hatch and shipped English into a Spanish UI
   * (YOD-589). Narrowing it means the next integration cannot repeat it
   * without the compiler objecting.
   */
  name: I18nString;
  desc: I18nString;
  icon: ReactNode;
  cloud?: boolean;
} & (
  | {
      setting: ReactNode;
    }
  | {
      link: string;
    }
);

const INTEGRATION_LIST = [
  {
    id: 'mcp-server' as const,
    name: 'com.affine.integration.mcp-server.name',
    desc: 'com.affine.integration.mcp-server.desc',
    icon: <img src={MCPIcon} />,
    setting: <McpServerSettingPanel />,
    cloud: false,
  },
  {
    id: 'vscode' as const,
    name: 'com.affine.integration.vscode.name',
    desc: 'com.affine.integration.vscode.desc',
    icon: <VSCodeIcon />,
    link: 'https://github.com/yoDEV-dev/vscode-extension-releases/releases',
    cloud: false,
  },
  {
    id: 'devto' as const,
    name: 'com.affine.integration.devto.name',
    desc: 'com.affine.integration.devto.desc',
    icon: <DevtoIcon />,
    setting: <DevtoSettingPanel />,
    cloud: false,
  },
  {
    id: 'github' as const,
    name: 'com.affine.integration.github.name',
    desc: 'com.affine.integration.github.desc',
    icon: <GithubIcon />,
    setting: <GitHubSettingPanel />,
    cloud: false,
  },
  {
    id: 'google' as const,
    name: 'com.affine.integration.google.name',
    desc: 'com.affine.integration.google.desc',
    icon: <GoogleIcon />,
    setting: <GoogleSettingPanel />,
    cloud: false,
  },
  {
    id: 'linear' as const,
    name: 'com.affine.integration.linear.name',
    desc: 'com.affine.integration.linear.desc',
    // ⚠️ Neutral icon, not Linear's mark. Two reasons, either sufficient:
    // linear.app/brand asks third-party integrations to request permission
    // (YOD-601, requested 2026-09-16, not yet granted), and the bundled
    // LinearLogoIcon paints its disc and its glyph both in currentColor, so it
    // renders as a featureless blob anyway. The card's title already says
    // "Linear", which is nominative use and needs no permission.
    icon: <IssueIcon />,
    setting: <LinearSettingPanel />,
    cloud: false,
  },
] satisfies IntegrationCard[];

type IntegrationId = Exclude<
  Extract<(typeof INTEGRATION_LIST)[number], {}>,
  false
>['id'];

export type IntegrationItem = Exclude<IntegrationCard, 'id'> & {
  id: IntegrationId;
};

export function getAllowedIntegrationList(isCloudWorkspace: boolean) {
  return INTEGRATION_LIST.filter(item => {
    if (!item) return false;
    const requiredCloud = 'cloud' in item && item.cloud;
    if (requiredCloud && !isCloudWorkspace) return false;
    return true;
  }) as IntegrationItem[];
}
